'use client';

import * as React from 'react';
import Link from 'next/link';
import {
  CalendarCheckIcon,
  FolderKanbanIcon,
  BadgeEuroIcon,
  BlocksIcon,
  BuildingIcon,
  ChevronRightIcon
} from 'lucide-react';

import { routes } from '@workspace/routes';
import { BlurFade } from '~/components/fragments/blur-fade';
import { GridSection } from '~/components/fragments/grid-section';
import { SiteHeading } from '~/components/fragments/site-heading';

type RelatedFeaturesProps = {
  exclude?: 'bedrijven' | 'activiteiten' | 'sales' | 'facturatie' | 'revenue';
};

const ALL_FEATURES = [
  {
    key: 'bedrijven' as const,
    icon: <BuildingIcon className="size-5 shrink-0" />,
    title: 'Bedrijven en Contactpersonen',
    description: 'Beheer al je relaties overzichtelijk op één centrale plek.',
    href: routes.marketing.features.BedrijvenEnContactpersonen,
    color: 'text-blue-600'
  },
  {
    key: 'activiteiten' as const,
    icon: <CalendarCheckIcon className="size-5 shrink-0" />,
    title: 'Activiteiten en Taken',
    description: 'Een gedeelde agenda en takenlijst voor je hele team.',
    href: routes.marketing.features.ActiviteitenEnTaken,
    color: 'text-blue-600'
  },
  {
    key: 'sales' as const,
    icon: <FolderKanbanIcon className="size-5 shrink-0" />,
    title: 'Sales Pipeline',
    description:
      'Een snel overzicht van alle leads en kansen? Je beheert ze met Emma.',
    href: routes.marketing.features.SalesPipeline,
    color: 'text-purple-600'
  },
  {
    key: 'facturatie' as const,
    icon: <BadgeEuroIcon className="size-5 shrink-0" />,
    title: 'Facturatie',
    description: 'Facturen maken en versturen via Emma.',
    href: routes.marketing.features.Facturatie,
    color: 'text-green-600'
  },
  {
    key: 'revenue' as const,
    icon: <BlocksIcon className="size-5 shrink-0" />,
    title: 'Revenue Hub',
    description: 'Direct inzicht in je verwachte omzet en betalingen.',
    href: routes.marketing.features.RevenueHub,
    color: 'text-orange-600'
  }
];

export function RelatedFeatures({
  exclude
}: RelatedFeaturesProps = {}): React.JSX.Element {
  const features = ALL_FEATURES.filter(
    (feature) => feature.key !== exclude
  ).slice(0, 4);

  return (
    <GridSection className="bg-muted/30">
      <div className="container space-y-12 py-20 md:py-32">
        <SiteHeading
          badge="Andere features"
          title="Ontdek ook onze andere krachtige functies"
          description="Emma biedt een compleet pakket aan tools om je bedrijf te laten groeien."
        />

        <div className="grid gap-6 md:grid-cols-2">
          {features.map((feature, index) => (
            <BlurFade
              key={feature.key}
              inView
              delay={0.1 + index * 0.1}
            >
              <Link
                href={feature.href}
                className="group flex items-start gap-4 rounded-xl border bg-card p-6 shadow-sm transition-all hover:border-primary/50 hover:shadow-md"
              >
                <div
                  className={`flex size-12 shrink-0 items-center justify-center rounded-xl border bg-background ${feature.color}`}
                >
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-lg font-semibold group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="mb-3 text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                  <div className="flex items-center text-sm font-medium text-primary">
                    Lees meer
                    <ChevronRightIcon className="ml-1 size-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </BlurFade>
          ))}
        </div>
      </div>
    </GridSection>
  );
}
